/**
 * @fileoverview Contact communications tab (MOR-529 Phase 6).
 *
 * Shown inside the contact detail panel: an AI summary + sentiment card for
 * the correspondence with this contact, above a chronological message
 * timeline pulled from the job mailbox's mirror tables. Keyed by the
 * contact's email address (messages from other addresses belonging to the
 * same person appear once contacts capture that address).
 */

import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { formatDistanceToNow } from "date-fns";
import { ArrowDownLeft, ArrowUpRight, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import { Badge, Button, Spinner } from "@/ui/primitives";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";

import { SENTIMENT_META } from "./sentimentMeta";

export function ContactCommsTab({
  jobId,
  emailAddress,
  jobContactId,
  onOpenThread,
  activeThreadId,
  showInsight = true,
}: {
  jobId: Id<"jobs">;
  emailAddress: string;
  jobContactId?: Id<"jobContacts">;
  /** Opens the message's thread in the reader pane (job mailbox ThreadView). */
  onOpenThread?: (threadId: Id<"mailThreads">) => void;
  /** Thread currently open in the reader pane — highlights its messages. */
  activeThreadId?: Id<"mailThreads"> | null;
  /** Hide the AI summary card when a parent surface already renders a
   *  combined one (MOR-841 consolidated Notes & Mail tab). */
  showInsight?: boolean;
}) {
  const hasEmail = emailAddress.trim().length > 0;

  const { data: messages, isLoading } = useQuery({
    ...convexQuery(api.mail.commsQueries.listMessagesForContact, {
      jobId,
      emailAddress,
    }),
    enabled: hasEmail,
  });
  const { data: insight } = useQuery({
    ...convexQuery(api.mail.commsQueries.getCommsInsight, {
      jobId,
      emailAddress,
    }),
    enabled: hasEmail && showInsight,
  });
  const { mutateAsync: requestInsight, isPending: generating } = useMutation({
    mutationFn: useConvexMutation(api.mail.commsQueries.requestCommsInsight),
  });

  if (!hasEmail) {
    return (
      <p className="text-sm text-muted-foreground">
        Add an email address to this contact to see their correspondence with
        the job mailbox.
      </p>
    );
  }

  const stale =
    insight?.status === "complete" &&
    insight.lastMessageAt > insight.generatedAt;
  const sentimentMeta = insight
    ? (SENTIMENT_META[insight.sentiment] ?? SENTIMENT_META.neutral)
    : null;

  const handleGenerate = async () => {
    try {
      await requestInsight({ jobId, emailAddress, jobContactId });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to generate summary"));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* AI summary + sentiment */}
      {showInsight && (
        <section className="rounded-lg border border-border bg-secondary/20 p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-wider">
              <Sparkles className="size-3.5" />
              Communications summary
            </h3>
            <div className="flex items-center gap-2">
              {sentimentMeta && insight?.status === "complete" && (
                <Badge className={cn("border-0", sentimentMeta.className)}>
                  {sentimentMeta.label}
                </Badge>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs"
                disabled={generating || insight?.status === "pending"}
                onClick={handleGenerate}
              >
                <RefreshCw
                  className={cn(
                    "size-3",
                    (generating || insight?.status === "pending") &&
                      "animate-spin",
                  )}
                />
                {insight ? "Refresh" : "Generate"}
              </Button>
            </div>
          </div>
          {insight?.status === "pending" ? (
            <p className="mt-2 text-sm text-muted-foreground">
              Analysing the correspondence…
            </p>
          ) : insight ? (
            <div className="mt-2 flex flex-col gap-1.5">
              <p className="whitespace-pre-wrap text-sm leading-relaxed">
                {insight.summary}
              </p>
              {insight.sentimentRationale && (
                <p className="text-xs text-muted-foreground">
                  {insight.sentimentRationale}
                </p>
              )}
              {stale && (
                <p className="text-xs text-amber-600">
                  New messages have arrived since this summary was generated.
                </p>
              )}
            </div>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              Generate an AI summary and sentiment read of the correspondence
              with this contact.
            </p>
          )}
        </section>
      )}

      {/* Timeline */}
      <section className="flex flex-col gap-2">
        <h3 className="font-sans text-xs font-semibold uppercase tracking-wider">
          Messages
        </h3>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : !messages || messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No correspondence with this contact through the job mailbox yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {messages.map(
              (message: {
                _id: string;
                threadId: Id<"mailThreads">;
                direction: "inbound" | "outbound";
                subject: string;
                bodyText: string;
                sentAt: number;
              }) => {
                const isActive = activeThreadId === message.threadId;
                const body = (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 text-xs font-medium">
                        {message.direction === "inbound" ? (
                          <ArrowDownLeft className="size-3 text-emerald-600" />
                        ) : (
                          <ArrowUpRight className="size-3 text-blue-600" />
                        )}
                        {message.subject || "(no subject)"}
                      </span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        {formatDistanceToNow(message.sentAt, {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                      {message.bodyText}
                    </p>
                  </>
                );
                return (
                  <li key={message._id}>
                    {onOpenThread ? (
                      <button
                        type="button"
                        onClick={() => onOpenThread(message.threadId)}
                        className={cn(
                          "block w-full rounded-md border border-border p-3 text-left transition-colors",
                          "hover:border-primary/40 hover:bg-muted/40 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                          message.direction === "outbound" && "bg-secondary/30",
                          isActive && "border-primary/50 bg-primary/5",
                        )}
                        title="Open this thread"
                      >
                        {body}
                      </button>
                    ) : (
                      <div
                        className={cn(
                          "rounded-md border border-border p-3",
                          message.direction === "outbound" && "bg-secondary/30",
                        )}
                      >
                        {body}
                      </div>
                    )}
                  </li>
                );
              },
            )}
          </ul>
        )}
      </section>
    </div>
  );
}
